import { useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Form, Input, List, Modal, Space, Tag, Typography, message } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

import { createSoftwareSupplier } from '@/api/config';
import type { SoftwareSupplier } from '@/types';

interface SoftwareSupplierSelectProps {
  value?: number[];
  onChange?: (value: number[]) => void;
  suppliers: SoftwareSupplier[];
  onCreated: (supplier: SoftwareSupplier) => void;
  disabled?: boolean;
  /** Form.Item 注入，用于把 label 关联到输入框 */
  id?: string;
}

interface CreateFormValues {
  name: string;
  short_name?: string;
  description?: string;
}

/**
 * 底软供应商选择器：多选。只读输入框里以 Tag 回显已选供应商，
 * 点击弹出左右分栏弹窗——左栏勾选列表，右栏是选中行的详情。
 * 作为 Form.Item 的受控子组件使用（value = 供应商 ID 数组）。
 */
const SoftwareSupplierSelect = ({
  value,
  onChange,
  suppliers,
  onCreated,
  disabled,
  id,
}: SoftwareSupplierSelectProps) => {
  const selectedIds = useMemo(() => value ?? [], [value]);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  // 只决定右栏展示谁的详情，不参与提交（勾选即时生效）
  const [detailId, setDetailId] = useState<number | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm] = Form.useForm<CreateFormValues>();

  const selected = useMemo(
    () => selectedIds
      .map((sid) => suppliers.find((s) => s.id === sid))
      .filter((s): s is SoftwareSupplier => !!s),
    [suppliers, selectedIds],
  );

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return suppliers;
    return suppliers.filter(
      (s) =>
        s.name.toLowerCase().includes(kw)
        || (s.short_name || '').toLowerCase().includes(kw),
    );
  }, [suppliers, keyword]);

  const detail = useMemo(
    () => suppliers.find((s) => s.id === detailId),
    [suppliers, detailId],
  );

  const openModal = () => {
    setDetailId(selectedIds[0]);
    setKeyword('');
    setCreating(false);
    createForm.resetFields();
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setCreating(false);
    createForm.resetFields();
  };

  const toggle = (supplierId: number) => {
    setDetailId(supplierId);
    onChange?.(
      selectedIds.includes(supplierId)
        ? selectedIds.filter((sid) => sid !== supplierId)
        : [...selectedIds, supplierId],
    );
  };

  const handleSaveCreate = async () => {
    let values: CreateFormValues;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const created = await createSoftwareSupplier({
        name: values.name.trim(),
        short_name: values.short_name?.trim() || undefined,
        description: values.description?.trim() || undefined,
      });
      onCreated(created);
      onChange?.([...selectedIds, created.id]);
      setDetailId(created.id);
      setCreating(false);
      createForm.resetFields();
      message.success('底软供应商已创建');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || '创建失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        id={id}
        onClick={disabled ? undefined : openModal}
        style={{
          minHeight: 32,
          padding: '4px 11px',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          background: disabled ? '#f5f5f5' : undefined,
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        {selected.length ? (
          selected.map((s) => (
            <Tag
              key={s.id}
              closable={!disabled}
              onClose={(e) => {
                e.stopPropagation();
                onChange?.(selectedIds.filter((sid) => sid !== s.id));
              }}
            >
              {s.name}
            </Tag>
          ))
        ) : (
          <Typography.Text type="secondary">点击选择底软供应商</Typography.Text>
        )}
        <SearchOutlined style={{ color: '#bfbfbf', marginLeft: 'auto' }} />
      </div>

      <Modal
        title="选择底软供应商（可多选）"
        open={open}
        onCancel={closeModal}
        width={720}
        destroyOnHidden
        footer={
          <Space>
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                setCreating(true);
                createForm.resetFields();
              }}
              disabled={creating}
            >
              新建底软供应商
            </Button>
            <Button type="primary" onClick={closeModal}>关闭</Button>
          </Space>
        }
      >
        <div style={{ display: 'flex', gap: 12, height: 360 }}>
          <div style={{ width: 260, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Input.Search
              allowClear
              placeholder="搜索供应商名称"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
              <List
                size="small"
                dataSource={filtered}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无供应商" /> }}
                renderItem={(item) => (
                  <List.Item
                    onClick={() => toggle(item.id)}
                    style={{
                      cursor: 'pointer',
                      paddingLeft: 12,
                      background: item.id === detailId ? '#e6f4ff' : undefined,
                    }}
                  >
                    <Space>
                      <Checkbox checked={selectedIds.includes(item.id)} />
                      <span>{item.name}</span>
                      {item.short_name ? (
                        <Typography.Text type="secondary">{item.short_name}</Typography.Text>
                      ) : null}
                    </Space>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {item.projects.length} 个项目
                    </Typography.Text>
                  </List.Item>
                )}
              />
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', paddingLeft: 12, borderLeft: '1px solid #f0f0f0' }}>
            {creating ? (
              <Form form={createForm} layout="vertical">
                <Form.Item
                  name="name"
                  label="供应商名称"
                  rules={[{ required: true, message: '请输入供应商名称' }]}
                >
                  <Input placeholder="如 Desay" />
                </Form.Item>
                <Form.Item name="short_name" label="简称">
                  <Input placeholder="选填，用于表格等窄空间展示" />
                </Form.Item>
                <Form.Item name="description" label="说明">
                  <Input.TextArea rows={3} placeholder="选填" />
                </Form.Item>
                <Space>
                  <Button type="primary" loading={saving} onClick={handleSaveCreate}>
                    保存
                  </Button>
                  <Button
                    onClick={() => {
                      setCreating(false);
                      createForm.resetFields();
                    }}
                  >
                    取消
                  </Button>
                </Space>
              </Form>
            ) : detail ? (
              <div>
                <Typography.Title level={5} style={{ marginTop: 0 }}>
                  {detail.name}
                </Typography.Title>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  {detail.short_name ? `简称：${detail.short_name}` : '简称：-'}
                </Typography.Paragraph>
                <Typography.Paragraph style={{ marginBottom: 12 }}>
                  {detail.description || '暂无说明'}
                </Typography.Paragraph>
                <Typography.Text strong>使用该供应商的项目</Typography.Text>
                <div style={{ marginTop: 8 }}>
                  {detail.projects.length ? (
                    <Space size={[4, 8]} wrap>
                      {detail.projects.map((p) => (
                        <Tag key={p.id}>{p.name}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Typography.Text type="secondary">暂无项目使用</Typography.Text>
                  )}
                </div>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="从左侧勾选供应商，这里显示详情"
                style={{ marginTop: 80 }}
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SoftwareSupplierSelect;
